import {
  disposeImportedModel,
  disposeImportedModelInstance,
} from '../../imported-model/render/imported-model-scene'
import type { ImportedModelResult } from '../../imported-model/loaders/types'

/**
 * 判断导入模型结果是否来自 `.puppy` 离线包。
 *
 * @param result 导入模型结果。
 * @returns 是否为离线包导入结果。
 */
function isPuppyPackageImportedModelResult(result: ImportedModelResult): boolean {
  const sourceAssetKey = 'sourceAssetKey' in result ? result.sourceAssetKey : undefined
  return result.fileInfo.kind === '.puppy' || sourceAssetKey?.startsWith('puppy:') === true
}

/**
 * 判断导入模型结果是否为 `.puppy` 子装配 scoped tab 克隆。
 *
 * @param result 导入模型结果。
 * @returns 是否为 scoped tab 结果。
 */
function isPuppyPackageScopedResult(result: ImportedModelResult): boolean {
  const sourceAssetKey = 'sourceAssetKey' in result ? result.sourceAssetKey : undefined
  return sourceAssetKey?.includes(':scope:') === true
}

/**
 * 去重导入模型结果引用。
 *
 * @param results 导入模型结果集合。
 * @returns 按首次出现顺序去重后的集合。
 */
function collectUniqueResults(results: Iterable<ImportedModelResult>): ImportedModelResult[] {
  return Array.from(new Set(results))
}

/**
 * 释放 `.puppy` 离线包导入结果集合。
 *
 * 设计说明：
 * `.puppy` 的 root 结果直接拥有 display.glb 解析出的可见 mesh 资源，而子装配 tab
 * 通过 `cloneWithSharedGeometry` 共享 root 的 geometry/material。释放时必须让 root
 * 走 full dispose，让 scoped tab 只移除实例关系，避免普通 GLB 的 cache-owned 规则误套到
 * 离线包上，也避免 scoped tab 重复释放共享资源。
 *
 * @param results 待释放结果集合。
 */
export function disposePuppyPackageImportedModelResults(
  results: Iterable<ImportedModelResult>,
): void {
  const puppyResults = collectUniqueResults(results).filter(isPuppyPackageImportedModelResult)
  if (puppyResults.length === 0) {
    return
  }

  const rootResults = puppyResults.filter((result) => !isPuppyPackageScopedResult(result))
  const scopedResults = puppyResults.filter(isPuppyPackageScopedResult)

  if (rootResults.length > 0) {
    scopedResults.forEach(disposeImportedModelInstance)
    rootResults.forEach(disposeImportedModel)
    return
  }

  const [fallbackFullDisposeResult, ...remainingScopedResults] = scopedResults
  if (fallbackFullDisposeResult) {
    disposeImportedModel(fallbackFullDisposeResult)
  }
  remainingScopedResults.forEach(disposeImportedModelInstance)
}
